/**
 * Please note! This file is a thin layer around the API, but as opposed to the `api.ts` file
 * this one can be manually extended, especially to handle cases where Swagger is not enough.
 * Do not add business logic here.
 * You can add types from other places but try to avoid circular dependencies.
 */

import Axios, { AxiosPromise, AxiosRequestConfig } from 'axios';
import { useState } from 'react';
import useSWR, { SWRConfiguration } from 'swr';

import { HttpHeader } from '#/service/constants';
import { ApiError } from '#/service/error/errors';
import { getDeviceInfo } from '#/service/user/userSessionManager';
import { changeUrlParams } from '#/service/utils';

import { axios } from './api';
import {
  Collection,
  FeedResponse,
  Item,
  PageLayout,
  ResourceItem,
  ResourceType,
} from './models';
import { makeSwrUnauthorizedRetry } from './utils';

interface SwrConfig extends SWRConfiguration {
  /* Configuration for axios fetcher */
  axios?: AxiosRequestConfig;
}

export function useDetailsResource(
  isLoggedIn: boolean,
  resourceType?: ResourceType,
  resourceId?: string,
  itemUrl?: string,
  $config?: SwrConfig
) {
  const url = itemUrl ?? `/api/core/catalog/${resourceType}/${resourceId}`;
  const { axios: $axiosConf, ...config } = $config || {
    revalidateOnFocus: false,
  };

  const { data, error, mutate } = useSWR<ResourceItem | Collection, Error>(
    [url, isLoggedIn],
    () => simpleGet<ResourceItem | Collection>(url, $axiosConf),
    config
  );

  return {
    data,
    isLoading: !error && !data,
    error: error as ApiError | undefined,
    mutate,
  };
}

export function usePageLayout(pageId?: string, $config?: SwrConfig) {
  const [finishedRevalidating, setFinishedRevalidating] = useState(false);
  const url = `/api/core/page/${pageId}`;
  const { axios: $axiosConf, ...config } = $config || {
    revalidateOnFocus: false,
  };

  const onErrorRetry = makeSwrUnauthorizedRetry(setFinishedRevalidating);

  const { data, error, mutate } = useSWR<PageLayout, ApiError>(
    pageId ? url : null,
    () => simpleGet<PageLayout>(url, $axiosConf),
    {
      ...config,
      onErrorRetry,
    }
  );

  return {
    data,
    isLoading: !error && !data,
    error: finishedRevalidating && error,
    mutate,
  };
}

export const resourceService = {
  get(
    resourceType?: ResourceType,
    resourceId?: string,
    itemUrl?: string,
    $config?: AxiosRequestConfig
  ) {
    const url = itemUrl ?? `/api/core/catalog/${resourceType}/${resourceId}`;

    return axios.request<ResourceItem | Collection>({
      url: url,
      method: 'GET',
      ...$config,
    });
  },

  getUpNext(
    url: string,
    $config?: AxiosRequestConfig
  ): AxiosPromise<UpNextResponse> {
    return axios.request<UpNextResponse>({
      url,
      method: 'GET',
      ...$config,
    });
  },
};

export function useFavoritesData(isLoggedIn: boolean, page: number) {
  const url = '/api/core/user/favorites';
  const cacheUrl = isLoggedIn ? `${url}?page=${page}` : null;

  const { data, error, mutate } = useSWR<FeedResponse, Error>(cacheUrl, () =>
    axios
      .request({
        url: url,
        method: 'GET',
        params: {
          page,
        },
      })
      .then((resp) => resp.data)
  );

  return {
    data,
    isLoading: !error && !data,
    error: error,
    mutate,
  };
}

export interface WatchHistoryItemResponse {
  data: Item;
}

export interface UpNextResponse {
  data: Item[];
  pageInfo?: {
    hasMore: boolean;
    page: number;
    pageSize: number;
    total: number;
  };
  _meta: UpNextMeta;
}

export interface UpNextMeta {
  /** If true, the content will be played automatically after the specified countdown. */
  autoplayFirst: boolean;
  countdownSeconds: number;
  type: UpextType;
  overlay: {
    images: {
      background: string;
      thumbnail: string;
    };
  };
}

export type UpextType = 'BINGE' | 'UP_NEXT';

export function simpleGet<T>(url: string, $axiosConf?: AxiosRequestConfig) {
  return axios.get<T>(url, $axiosConf).then((resp) => resp.data);
}

/**
 * Send beacon request. It uses different Axios instance, so it's not gonna use our interceptors
 * Additionally, beacon request is silent, so it won't trigger any errors. Fire and forget.
 */
export function sendBeacon(url: string, body?: object) {
  return Axios.post<void>(url, body, {
    headers: {
      [HttpHeader.DeviceInfo]: JSON.stringify(getDeviceInfo()),
    },
  })
    .then((res) => res.data)
    .catch(() => {});
}

export const endpointUrls = {
  getWatchHistory: (page = 1, pageSize = 20) =>
    pathWithParams('/api/core/user/watch-history', {
      page,
      pageSize,
    }),
  getFavorites: (page = 1, pageSize = 20) =>
    pathWithParams('/api/core/user/favorites', {
      page,
      pageSize,
    }),
  search: (query: string, page = 1, pageSize = 20) =>
    pathWithParams('/api/core/search', {
      q: query,
      page,
      pageSize,
    }),
};

export const pathWithParams = (
  path: string,
  queryParams = {},
  baseQuery: string = window.location.search,
  // TODO: move these keys to an enum
  keysToPersist: string[] = [
    'orgHash',
    'variationHash',
    'appId',
    'env',
    'countryCode',
    'regionCode',
  ]
) => {
  const urlParams = new URLSearchParams(baseQuery);

  const mergedParams = keysToPersist.reduce(
    (acc, p) => (urlParams.has(p) ? { ...acc, [p]: urlParams.get(p) } : acc),
    queryParams
  );

  return changeUrlParams(path, mergedParams);
};
